import classNames from "classnames/dedupe";
import React, { FC, ReactNode } from "react";

import {
  Image,
  IImageData,
  IMediaData,
} from "../../../Utility/MediaBuilder/Image";
import { useGallery } from "../useGallery";
import ICustomImageData from "../customImageDataInterface";
import { Carousel } from "../../../ActionsSelectors/Carousel";
import { SvgIcon } from "../../../Icons";
import { VideoPlayIcon } from "../../../../icons";

export interface IProductGalleryProps {
  classes?: {
    /** Styles applied to outer 'a' element. */
    root?: string;
  };
  /**
   * Array of image data.
   * Use key customImage to replace the default Image component
   */
  images?: ICustomImageData[];
  /**
   * Highlight the selected image
   */
  activeItem?: IImageData;
  /**
   * Property to render gallery as vertical carousel
   */
  isCarousel?: boolean;
  /**
   * Applies carouselHeight of carousel to be the same as main image carouselHeight
   */
  carouselHeight?: number;
  children?: ReactNode;
  /** Enables or disables 360
  plugin360: any;
  "video":
  /**
   * A callback function for when image is clicked by user
   */
  onSelect?(image: IImageData): void;
  /**
   * If videoGalleryItem is provided it is added to the list of gallery items
   */
  videoGalleryItem?: React.ReactElement;
  /**
   * If roundViewGalleryItem is provided it is added to the list of gallery items
   */
  roundViewGalleryItem?: React.ReactElement;
}

const ProductGallery: FC<IProductGalleryProps> = ({
  images,
  activeItem,
  classes,
  isCarousel,
  carouselHeight,
  children,
  onSelect,
  videoGalleryItem,
  roundViewGalleryItem,
}) => {
  const [selectedImage, handleOnSelect, imageSet] = useGallery(
    images,
    activeItem,
    onSelect,
  );

  const renderMediaItem = (item: IMediaData) => {
    return item.video ? (
      <SvgIcon icon={VideoPlayIcon} />
    ) : (
      <Image src={item.src} alt={item.alt} id={item.id} />
    );
  };

  const imagesList = imageSet.map((item) => (
    <div
      className={classNames("product-gallery--item", {
        active: selectedImage.id === item.id,
        "w-60px h-60px me-0": isCarousel,
      })}
      key={item.id}
      data-testid="gallery-item"
      onClick={() => handleOnSelect(item)}
    >
      {item.customImage ? item.customImage : renderMediaItem(item)}
    </div>
  ));

  const getFinalItemsList = () => {
    if (videoGalleryItem) {
      imagesList.push(videoGalleryItem);
    }
    if (roundViewGalleryItem) {
      imagesList.push(roundViewGalleryItem);
    }

    return imagesList;
  };

  const galleryItemsToShow = getFinalItemsList();

  return (
    <div
      className={classNames("product-gallery", classes?.root, {
        "flex-column m-0 ms-6 me-7": isCarousel,
      })}
      data-testid="product-gallery"
      style={{
        height: isCarousel && carouselHeight,
      }}
    >
      {isCarousel ? (
        <Carousel variant="product-image-gallery-carousel">
          {galleryItemsToShow}
        </Carousel>
      ) : (
        galleryItemsToShow
      )}

      {children}
    </div>
  );
};

export default ProductGallery;
